// =============================================================================
// AuthSession – die einzige Stelle, die Tokens kennt (Entscheidung 39)
// =============================================================================
// Die restliche App fragt nur: "Wer ist angemeldet?" und "Gib mir ein gültiges
// Access Token". WO die Tokens liegen, entscheidet allein die Umsetzung dieser
// Schnittstelle. v1 (Variante A): alles nur im Speicher. Der spätere Umstieg auf
// ein httpOnly-Cookie (Variante C) tauscht nur die Umsetzung aus.
//
// Ein "interface" beschreibt in TypeScript nur die Form eines Objekts – welche
// Methoden es mit welchen Typen hat. Zwei Umsetzungen erfüllen sie:
//   cognito-session.ts – echter Login über Cognito (dev:aws, Produktion)
//   local-session.ts   – Test-Token vom lokalen Dev-Server (nur Entwicklung)

// Anbieter, die die eigene Startseite als Button zeigt (Entscheidung 37). Der
// Name muss exakt dem IdP-Namen im User Pool entsprechen.
export type IdentityProvider = "Google" | "Microsoft";

export interface AuthUser {
  readonly userId: string;
  readonly email: string | undefined;
}

export interface LoginOptions {
  // Ohne Anbieter: Cognito Managed Login ("Andere Anmeldemethoden").
  readonly provider?: IdentityProvider;
  // Relativer Pfad, zu dem nach dem Login zurückgesprungen wird.
  readonly returnTo: string;
}

export interface AuthSession {
  readonly kind: "cognito" | "local";

  // Stellt eine bestehende Sitzung wieder her, OHNE umzuleiten. null = nicht angemeldet.
  // "Promise<T>" ist das Versprechen auf einen Wert, der später (asynchron) kommt;
  // mit "await" wartet man darauf.
  restore(): Promise<AuthUser | null>;

  // Startet den Login. Cognito leitet die Seite um (das Promise endet dann nie),
  // der lokale Modus meldet direkt an und liefert den Benutzer.
  login(options: LoginOptions): Promise<AuthUser>;

  // Schließt den Login nach dem Rücksprung auf /callback ab (nur Cognito).
  completeLogin(url: string): Promise<{ user: AuthUser; returnTo: string }>;

  // Gültiges Access Token oder null. Erneuert abgelaufene Tokens selbst.
  getAccessToken(): Promise<string | null>;

  // Meldet ab und verlässt die Seite (Cognito: über dessen Logout-Endpunkt,
  // lokal: Neuladen von /). `Promise<never>`: das Promise endet nie, weil der
  // Browser die Seite verlässt; Code danach läuft nicht. Der Neustart der App
  // leert dabei auch Tokens und Daten-Cache im Speicher.
  logout(): Promise<never>;

  // Zuletzt genutzter Anbieter (nur der Name, kein Token). Erlaubt nach einem
  // Neuladen die Weiterleitung ohne Umweg über die Startseite.
  lastProvider(): IdentityProvider | null;
}

// Prüft einen Rücksprungpfad: nur relative Pfade derselben Seite, damit kein
// Link nach dem Login auf eine fremde Adresse umleiten kann (Open Redirect).
export function safeReturnTo(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) {
    return undefined;
  }
  return value;
}
